import React, { useState, useRef, useEffect } from "react";
import SendIcon from "@mui/icons-material/Send";
import axios from "axios";
import { BASE_URL } from "./apiConfig";
import DOMPurify from 'dompurify';

interface ChatFrameProps {
  onClose: () => void;
  initialMessage?: string | null; // Add this
}

// Removed the AskResponse type as it's no longer needed

// Define the expected response type from the backend
type ChatResponse = {
  data: string;
};

function formatTime(dateStr: string) {
  const date = new Date(dateStr);
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

// Update the formatMessageText function to use single asterisks for bold
const formatMessageText = (text: string) => {
  // First handle single asterisks for bold formatting
  let formattedText = text.replace(
    /\*(.*?)\*/g,
    '<span class="font-bold text-black">$1</span>'
  );

  // Replace newlines with line breaks
  formattedText = formattedText.replace(/\n/g, "<br />");
  
  // Sanitize HTML to prevent XSS attacks
  return DOMPurify.sanitize(formattedText);
};

const ChatFrame: React.FC<ChatFrameProps> = ({ onClose, initialMessage }) => {
  const [messages, setMessages] = useState<
    { sender: "user" | "bot"; text: string; timestamp: string; thinking?: boolean }[]
  >([]);
  const [input, setInput] = useState("");
//   const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Scroll to the bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Hide welcome/chips after first user message
  const showWelcome = messages.length === 0;

  const getHistoryString = () => {
    // Get last 5 exchanges (user/bot pairs)
    const history = [];
    for (let i = messages.length - 1; i >= 0 && history.length < 10; i--) {
      const msg = messages[i];
      if (msg.sender === "user") history.unshift(`User: ${msg.text}`);
      if (msg.sender === "bot") history.unshift(`Assistant: ${msg.text}`);
    }
    return history.join("\n");
  };

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim()) return;
    const now = new Date().toISOString();
    setMessages((prev) => [
      ...prev,
      { sender: "user", text: input.trim(), timestamp: now },
      { sender: "bot", text: "Thinking...", timestamp: now, thinking: true }, // Add this
    ]);
    setInput("");
    // setLoading(true);

    const payload = {
      message: input.trim(),
      history: getHistoryString(),
    };

    let timeoutReached = false;
    const timeout = setTimeout(() => {
      timeoutReached = true;
      setMessages((prev) => [
        ...prev.slice(0,-1),
        {
          sender: "bot",
          text: "Please rephrase the question.",
          timestamp: new Date().toISOString(),
        },
      ]);
    //   setLoading(false);
    }, 120000); // 2 minutes

    try {
      const res = await axios.post<ChatResponse>(`${BASE_URL}/chat`, payload, {
        timeout: 119000,
      });
      if (!timeoutReached) {
        clearTimeout(timeout);
        console.log("Response from /chat:", res.data);
        setMessages((prev) => [
          ...prev.slice(0, -1),
          {
            sender: "bot" as const,
            text: res.data.data, // Extract the actual message from res.data.data
            timestamp: new Date().toISOString(),
            thinking: false
          }
        ]);
        // setLoading(false);
      }
    } catch (err) {
      if (!timeoutReached) {
        clearTimeout(timeout);
        setMessages((prev) => [
          ...prev.slice(0,-1),
          {
            sender: "bot" as const,  // Add type assertion
            text: "server Not availble",
            timestamp: new Date().toISOString(),
            thinking: false
          }
        ]);
        // setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (initialMessage) {
      setInput(initialMessage);
      // Simulate sending the message
      setTimeout(() => {
        document.getElementById("chat-input-form")?.dispatchEvent(new Event("submit", { bubbles: true }));
      }, 100);
    }
  }, [initialMessage]);

  // Play sound when a new bot response arrives (not "Thinking...")
  useEffect(() => {
    if (
      messages.length > 0 &&
      messages[messages.length - 1].sender === "bot" &&
      !messages[messages.length - 1].thinking
    ) {
      if (audioRef.current) {
        audioRef.current.volume = 0.13; // Set volume to 10%
        audioRef.current.play();
      }
    }
  }, [messages]);



  return (
    <div className="fixed inset-0 w-full h-full md:inset-auto md:bottom-8 md:right-8 md:w-[370px] md:h-[500px] bg-white rounded-none md:rounded-2xl shadow-none md:shadow-2xl flex flex-col z-50 border-none md:border md:border-gray-200">
      {/* Add audio element */}
      <audio ref={audioRef} src="/popup.wav" preload="auto" />
      {/* Header */}
      <div
        className="flex items-center justify-between px-4 py-3 rounded-t-none md:rounded-t-2xl"
        style={{ background: "#2E475D" }}
      >
        <div className="flex items-center gap-2">
          <img
            src="/tatatrent_bot.png"
            alt="Bot"
            className="w-8 h-8 rounded-full"
          />
          <span className="text-white font-semibold">Trent Bot</span>
        </div>
        <svg
          onClick={onClose}
          xmlns="http://www.w3.org/2000/svg"
          className="h-8 w-8 p-1 rounded-full cursor-pointer text-white hover:text-black  transition-colors"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
          aria-label="Close chat"
          tabIndex={0}
          role="button"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M6 6l12 12M6 18L18 6"
          />
        </svg>
      </div>
      {/* Chat Body */}
      <div className="flex-1 px-3 md:px-4 pt-4 pb-4 overflow-y-auto bg-[#f8fafc]">
        <div className="mb-4 mt-8 md:mt-0 flex flex-col gap-4">
          {showWelcome ? (
            <>
              {/* Bot icon and welcome message */}
              <div className="flex items-start gap-2">
                <img
                  src="/tatatrent_bot.png"
                  alt="Bot"
                  className="w-10 h-10 rounded-full mt-1 border-2 border-white shadow ml-[-8px]"
                />
                <div
                  className="bg-[#eaf0f6] text-[#425B76] rounded-[10px] px-5 py-3 shadow-sm"
                  style={{
                    fontFamily:
                      'system-ui, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                    fontSize: 14,
                    fontWeight: 400,
                    lineHeight: "24px",
                    maxWidth: "calc(100% - 48px)",
                    minWidth: 0,
                    wordBreak: "break-word",
                    boxSizing: "border-box",
                    display: "inline-flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                  }}
                >
                  🛍️ Hi! I'm Trent, your Retail Analytics Assistant.  
How can I help with your retail insights today?
                </div>
              </div>
             
            </>
          ) : (
            // Render chat messages
            messages.map((msg, idx) =>
              msg.sender === "bot" ? (
                <div
                  key={idx}
                  className="flex items-start gap-1"
                  style={{ alignItems: "flex-start" }}
                >
                  <img
                    src="/tatatrent_bot.png"
                    alt="Bot"
                    className="w-10 h-10 rounded-full mt-1 border-2 border-white shadow ml-[-8px] flex-shrink-0"
                  />
                  <div
                    className="bg-[#eaf0f6] text-[#425B76] rounded-[10px] px-4 py-2 shadow-sm"
                    style={{
                      fontFamily:
                        'system-ui, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                      fontSize: 14,
                      fontWeight: 400,
                      lineHeight: "22px",
                      maxWidth: "95%", // Increased from 75% to 95%
                      minWidth: "120px",
                      marginLeft: "4px",
                      marginRight: "auto",
                      marginTop: "15px",
                      boxSizing: "border-box",
                      display: "inline-flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                    }}
                  >
                    {msg.thinking ? (
                      <span style={{ display: "flex", alignItems: "center", minHeight: 24 }}>
                        <span
                          style={{
                            display: "inline-block",
                            width: 20,
                            height: 20,
                            marginRight: 8,
                            border: "3px solid #00bcd4",
                            borderRadius: "50%",
                            borderTop: "3px solid #eaf0f6",
                            animation: "spin 1s linear infinite",
                          }}
                        />
                        <style>
                          {`
                            @keyframes spin {
                              0% { transform: rotate(0deg);}
                              100% { transform: rotate(360deg);}
                            }
                          `}
                        </style>
                        <span style={{ color: "#425B76", fontWeight: 500 }}>Thinking...</span>
                      </span>
                    ) : (
                      <>
                        <div
                          dangerouslySetInnerHTML={{ __html: formatMessageText(msg.text) }}
                        />
                        <span
                          style={{
                            fontSize: 12,
                            color: "#7a8ca3",
                            marginTop: 4,
                            alignSelf: "flex-end",
                          }}
                        >
                          {formatTime(msg.timestamp)}
                        </span>
                      </>
                    )}
                  </div>
                </div>
              ) : (
                <div key={idx} className="flex items-end justify-end gap-2">
                  <div
                    className="koOvyS"
                    style={{
                      backgroundColor: "#2E475D",
                      color: "#fff",
                      borderRadius: "10px",
                      maxWidth: "max(100% - 48px, 284px)",
                      float: "right",
                      fontFamily:
                        'system-ui, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                      fontSize: 14,
                      fontWeight: 400,
                      lineHeight: "24px",
                      boxSizing: "border-box",
                      display: "inline-flex",
                      flexDirection: "column",
                      alignItems: "flex-end",
                      padding: "10px 16px",
                      minHeight: "32px",
                      wordBreak: "break-word",
                      overflowWrap: "break-word",
                    }}
                  >
                    {msg.text}
                    <span
                      style={{
                        fontSize: 12,
                        color: "#fff", // <-- changed from #cfd8dc to #fff
                        marginTop: 4,
                        alignSelf: "flex-end",
                      }}
                    >
                      {formatTime(msg.timestamp)}
                    </span>
                  </div>
                </div>
              )
            )
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>
      {/* Chat Input */}
      <div className="max-w-full bg-white flex-shrink-0 mx-4 mb-4 border border-[#99ACC2] rounded-[28px] p-2">
        <form className="flex items-center" onSubmit={handleSend} id="chat-input-form">
          <input
            type="text"
            placeholder="Write a message"
            className="flex-1 bg-transparent border-none outline-none px-2 font-sans text-[14px] text-black placeholder-gray-500"
            style={{
              fontFamily: 'inherit',
              fontSize: 14,
              color: '#000000', // Explicit black text color
              backgroundColor: 'transparent',
            }}
            value={input}
            onChange={(e) => setInput(e.target.value)}
          />
          <SendIcon
            sx={{
              fontSize: 24,
              color: "#425B76",
              cursor: "pointer",
              ml: 2,
              transition: "color 0.2s",
              "&:hover": { color: "#00c853" },
            }}
            onClick={handleSend}
          />
        </form>
      </div>
    </div>
  );
};

export default ChatFrame;